/* eslint-disable react/no-array-index-key */
import React, { useState, useMemo, useCallback } from "react";
import { arrayOf, bool, shape, string } from "prop-types";
import NextLink from "@insightfulscience/smart-react/NextLink";
import CardBlock from "@insightfulscience/atomic-react/CardBlock";
import { CardBlockRow, CardBlockCol } from "@insightfulscience/atomic-react/CardBlock/Grid";
import { useLocale } from "@insightfulscience/smart-react/i18";
import TabTitle from "@insightfulscience/atomic-react/Tabs/Title";
import { TabCol } from "@insightfulscience/atomic-react/Tabs/Grid";
import Tabs from "@insightfulscience/atomic-react/Tabs";
import getContentProps from "@insightfulscience/contentful/utils/getContentProps";
import { hasHtmEnding } from "@insightfulscience/contentful/utils/url/preparePostLink";
import RichTextLink from "@insightfulscience/contentful/ui/RichTextLink";
import { setDefaultCardBlockImage } from "@insightfulscience/contentful/utils/imagesAPI";
import Block from "@insightfulscience/atomic-react/Block";
import Image from "@insightfulscience/atomic-react/Image";
import Inline from "@insightfulscience/atomic-react/Inline";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Icon from "@insightfulscience/atomic-react/Icon";

const FeaturedContentItems = ({ items = [], isImgFluid = false, withFilter = false, ...props }) => {
	const [tB] = useLocale("blog");
	const [tR] = useLocale("resources");
	const [tP] = useLocale("plugins");
	const contentTypes = useMemo(() => [...new Set(items.map(item => item.sys_type))], [items]);
	const [contentType, setContentType] = useState(contentTypes[0]);

	const filteredItems = useMemo(
		() => (withFilter ? items.filter(item => item.sys_type === contentType) : items),
		[withFilter, items, contentType],
	);

	const changeContentType = useCallback(type => () => setContentType(type), [setContentType]);
	return (
		<>
			{withFilter && (
				<Tabs center mx isBorderBottom={false} spacing={{ my: 12 }}>
					{contentTypes.map(type => (
						<TabCol key={type} lg={3} md={5} onClick={changeContentType(type)}>
							<TabTitle
								sx={{
									fontWeight: "bold",
									textAlign: "center",
								}}
								active={contentType === type}
							>
								{tR(`CONTENT_TYPES.${type}`)}
							</TabTitle>
						</TabCol>
					))}
				</Tabs>
			)}
			{filteredItems.length > 0 && (
				<CardBlockRow>
					{filteredItems.map((item, index) => {
						const {
							slug = "",
							externalUrl = "",
							teaser,
							lead,
							type,
							title,
							thumbnail,
							listThumbnails,
							articleType,
							image,
							listImages,
							sys_type: sysType,
							description: itemDescription,
							sys_id: sysId,
						} = item;
						const description = teaser || lead || itemDescription;
						const contentProps = getContentProps(sysType, type);
						const DefaultLinkComponent =
							contentProps && contentProps.alias ? NextLink : RichTextLink;
						const LinkComponent = hasHtmEnding(slug) ? RichTextLink : DefaultLinkComponent;

						switch (sysType) {
							case "pageBlog":
								return (
									<CardBlockCol key={`${sysId}_${index}`}>
										<CardBlock
											titleText={title}
											href={
												contentProps && contentProps.alias ? `${contentProps.alias}/${slug}` : "#"
											}
											isImgFluid
											imgSrc={thumbnail}
											imgSrcList={setDefaultCardBlockImage(listThumbnails)}
											descriptionText={lead}
											linkButtonText={tB("READ_MORE")}
											cardType={articleType}
											utility={{ bgColor: "white" }}
										/>
									</CardBlockCol>
								);
							case "pagePlugins":
								return (
									<CardBlockCol
										key={`${sysId}_${index}`}
										utility={{
											display: "flex-vertical",
											justify: "between",
											border: { width: 1, color: "dotmatics-1" },
										}}
									>
										<Block>
											<DefaultLinkComponent
												href={
													contentProps && contentProps.alias ? `${contentProps.alias}/${slug}` : "#"
												}
												sx={{ fontColor: "black", hover: { fontColor: "3" } }}
												utility={{ display: "block" }}
												className="link--underline-none"
											>
												<Image isBaseUrl={false} src={thumbnail || ""} sizing={{ w: "full" }} />
												<Block spacing={{ py: 3, px: 5 }} utility={{ display: "flex-vertical" }}>
													<Inline>{title}</Inline>
												</Block>
											</DefaultLinkComponent>
											<Paragraph
												spacing={{ mt: 0, mx: 5 }}
												sx={{ fontColor: "black--custom-1", fontSize: "s" }}
											>
												{description}
											</Paragraph>
										</Block>
										<Block spacing={{ mt: 3, my: 5, mx: 5 }}>
											<DefaultLinkComponent
												href={
													contentProps && contentProps.alias ? `${contentProps.alias}/${slug}` : "#"
												}
											>
												{tP("DOWNLOAD")} <Icon name="angle-right" color="3" />
											</DefaultLinkComponent>
										</Block>
									</CardBlockCol>
								);
							default:
								return (
									<CardBlockCol key={`${sysId}_${index}`}>
										<CardBlock
											isImgFluid={isImgFluid}
											LinkComponent={LinkComponent}
											cardType={contentProps?.typePlaceholder || type || ""}
											titleText={title}
											imgSrc={image}
											imgSrcList={setDefaultCardBlockImage(listImages)}
											descriptionText={description}
											href={
												contentProps && contentProps.alias
													? `${contentProps.alias}/${slug}`
													: externalUrl
											}
											linkButtonText={contentProps?.linkPlaceholder || ""}
											utility={{ radius: 0 }}
											imgClassName="border-radius-0px"
											{...props}
										/>
									</CardBlockCol>
								);
						}
					})}
				</CardBlockRow>
			)}
		</>
	);
};

FeaturedContentItems.propTypes = {
	items: arrayOf(
		shape({
			sys_type: string,
			slug: string,
			externalUrl: string,
			teaser: string,
			description: string,
			title: string,
			image: string,
			type: string,
			lead: string,
			articleType: string,
			thumbnail: string,
		}),
	),
	isImgFluid: bool,
	imgClassName: string,
	withFilter: bool,
};

export default FeaturedContentItems;
